import { AuthSupplier } from '../../auth/auth.store';
import { CurrencySymbolPosition } from '../../common.model';
import { ErrorCode } from '../../errors/error-codes';
import { logger } from '../../logging.service';
import { retryingFetch } from '../../retrying-fetch';

const LOCALES_TO_FORMAT: string[] = ['de_DE'];

export class LocalizationService {
  public async loadLabels(moduleName: string, admin: boolean, authSupplier: AuthSupplier) {
    const { apiUrl, headers } = await authSupplier();
    try {
      const response = await retryingFetch(
        apiUrl + `/${moduleName.toLowerCase()}/v1/${admin ? 'admin/' : ''}label/list?translate=true`,
        { headers }
      );

      if (!response.ok) {
        logger.error(
          ErrorCode.LabelBadRequest,
          `Couldn't load labels for ${moduleName} with server status ${response.status}`
        );
        return {};
      }
      try {
        const content: { labels: Record<string, string> } = await response.clone().json();
        return content.labels;
      } catch (error) {
        const body = await response.text();
        logger.error(
          ErrorCode.LabelParseError,
          `Labels of ${moduleName} JSON Parse failed with response: ${JSON.stringify({
            headers: response.headers,
            body,
            status: response.status,
            statusText: response.statusText,
          })}`
        );
        return {};
      }
    } catch (error) {
      logger.error(ErrorCode.LabelFetchFailed, `Error fetching Labels for ${moduleName}`, error);
      return {};
    }
  }

  public getPriceFormat(
    locale: string = this.getUserPreferredLocale(),
    currencyCode?: string,
    currencySymbolPosition?: CurrencySymbolPosition,
    currencyValue?: string,
    maximumFractionDigits = 0,
    minimumFractionDigits = 0
  ) {
    if (!currencyCode) {
      return this.getNumberFormat(locale);
    }
    const safeLocale = locale && locale.replace('_', '-');
    return {
      format: (value: number): string => {
        const parts = new Intl.NumberFormat(safeLocale, {
          style: 'currency',
          currency: currencyCode,
          maximumFractionDigits,
          minimumFractionDigits,
        }).formatToParts(value);

        const literal = parts.find((element) => element.type === 'literal')?.value;
        const currency = currencyValue ?? parts.find((element) => element.type === 'currency')?.value;
        let result: (string | undefined)[];

        if (currencySymbolPosition) {
          result = parts
            .filter((element) => element.type !== 'literal' && element.type !== 'currency')
            .map((entry) => entry.value);
          result =
            currencySymbolPosition === 'BEFORE' ? [currency, literal, ...result] : [...result, literal, currency];
        } else {
          result = parts.map((entry) => {
            if (entry.type === 'literal') {
              return literal;
            } else if (entry.type === 'currency') {
              return currency;
            }
            return entry.value;
          });
        }
        return result.join('');
      },
    };
  }

  public getNumberFormat(locale: string = this.getUserPreferredLocale()) {
    const safeLocale = locale && locale.replace('_', '-');
    return new Intl.NumberFormat(safeLocale, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });
  }

  public formatNumber(value?: number, locale?: string) {
    if (value === undefined || value === null) {
      return value;
    }
    if (locale && LOCALES_TO_FORMAT.includes(locale)) {
      const safeLocale = locale && locale.replace('_', '-');
      const numberFormat = new Intl.NumberFormat(safeLocale, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      });
      return numberFormat.format(value);
    }
    return value;
  }

  public parseLocalizedCurrencyString(value: string, locale?: string) {
    const safeLocale = locale && locale.replace('_', '-');
    // Get the number format for the specified locale
    const numberFormat = new Intl.NumberFormat(safeLocale);

    // Get the decimal separator for the specified locale
    const decimalSeparator = numberFormat.format(0.1).replace(/\d/g, '');

    // Remove the thousands separator from the number format pattern
    const pattern = numberFormat.format(1000);
    const thousandsSeparator = pattern.replace(/\d/g, '');

    // Remove any non-digit or non-decimal separator characters from the string
    const cleanValue = value.replace(new RegExp(`[^\\d${decimalSeparator}]`, 'g'), '');

    // Replace the thousands separator with an empty string
    const parsedValue = thousandsSeparator
      ? cleanValue.replace(new RegExp(`\\${thousandsSeparator}`, 'g'), '')
      : cleanValue;

    // Parse the cleaned value to a number
    const numberValue = parseFloat(parsedValue.replace(decimalSeparator, '.'));

    return isNaN(numberValue) ? 0 : numberValue;
  }

  public formatLocalizedPhoneString(value: string, locale?: string) {
    switch (locale) {
      case 'hu_HU':
        return this.phoneFormatHU(value);
    }
    return value;
  }

  private phoneFormatHU(input: string) {
    const cleaned = ('' + input).replace(/\D/g, ''); // Remove all non-numeric characters
    const match = cleaned.match(/^(\d{2})(\d{1,2})(\d{6,7})$/); // Match groups of 2, 1 or 2, and 6 or 7 digits

    if (match) {
      return `${match[1]} ${match[2]} ${match[3]}`;
    }

    return input;
  }

  public getUnitFormat(
    locale: string = this.getUserPreferredLocale(),
    unit: string,
    maximumFractionDigits = 0,
    minimumFractionDigits = 0
  ) {
    const safeLocale = locale && locale.replace('_', '-');
    return new Intl.NumberFormat(safeLocale, {
      style: 'unit',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      unit,
      maximumFractionDigits,
      minimumFractionDigits,
    });
  }

  private getUserPreferredLocale() {
    return window.navigator.language || 'en';
  }
}

export const localizationSvc = new LocalizationService();
